import type { UpdateContactInput } from 'app/generated/gql/graphql'
import { CheckCircleIcon } from '@heroicons/react/20/solid'
import { ColumnDef } from '@tanstack/react-table'
import { XmarkCircle } from 'iconoir-react'
import { compact } from 'lodash'
import moment from 'moment'
import { AdPlatformMappingCell } from 'app/components/contacts/AdPlatformMappingCell'
import { ChannelMappingCell } from 'app/components/contacts/ChannelMappingCell'
import { LazyAdCampaign } from 'app/components/contacts/LazyAdCampaign'
import { DateComponent } from 'app/components/DateComponent'
import { LazyLocationCell } from 'app/components/LazyLocationCell'
import { LazyWorkspaceCell } from 'app/components/LazyWorkspaceCell'
import PhoneNumber from 'app/components/PhoneNumber'
import { ContactCell } from 'app/components/shared/ContactCell'
import { AnchorCell } from 'app/components/table/AnchorCell'
import { InlineEditCell } from 'app/components/table/InlineEditCell'
import { StaffAssignCell } from 'app/components/table/StaffAssignCell'
import TooltipWrapper from 'app/components/table/TooltipWrapper'
import { UserAssignCell } from 'app/components/table/UserAssignCell'
import TagsCell from 'app/components/tags/TagsCell'
import type { ContactJson } from 'app/types/entities'
import type { Permission } from 'app/types/entities'
import { notifySuccess } from 'app/services/notify.client'
import { graphql } from 'app/generated/gql'
import { gqlMutation } from 'app/lib/graphqlClient'

const UPDATE_CONTACT_MUTATION = graphql(`
  mutation UpdateContactMarketing($id: ID!, $input: UpdateContactInput!) {
    updateContact(id: $id, input: $input) {
      id
    }
  }
`)
import { bg } from 'lib/background'
import { getLeadStatusColorMap } from 'lib/colorMappings'
import { currentUrlWithParam } from 'lib/currentUrlWithParam'
import { OptionType } from 'types/OptionType'

const Address = (props) => (
  <>{compact([props.row.original.addressLine1?.trim(), props.row.original.addressLine2?.trim()]).join(`, `)}</>
)
const Age = (props) => (
  <>{props.row.original.birthDate && (moment().diff(props.row.original.birthDate, `years`) || `0`)}</>
)

export const defaultColumns = (
  contactHandler: (contactId: string, patientIds?: string[]) => void,
  updateContact: (contact: UpdateContactInput & { id?: string }) => void,
  closeDrawerHandler: () => void,
  checkPermission: (permission: Permission | Permission[]) => boolean,
  addMarketingFilters: boolean,
  isSMC: boolean,
  isSuper: boolean,
  staffV2Enabled?: boolean,
  staffDashboardV2?: boolean,
): ColumnDef<ContactJson, any>[] => {
  let addMarketing = false
  let addUtm = false
  const marketingColumns: ColumnDef<ContactJson, any>[] = [
    {
      id: `platform`,
      header: `Ad Platform`,
      accessorKey: `platform`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `platform.keyword`,
        ControlledComponent: (row) => <AdPlatformMappingCell contact={row} />,
      },
    },
    {
      id: `Ad Campaign`,
      header: `Ad Campaign`,
      enableSorting: false,
      cell: (props) => <LazyAdCampaign campaignId={props.getValue()} />,
      accessorKey: `trackableCampaignId`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
      },
    },
  ]
  const utmColumns: ColumnDef<ContactJson, any>[] = [
    {
      id: `utmSource`,
      header: `Source`,
      accessorKey: `utmSource`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `utmSource.keyword`,
        tooltipClassName: `lowercase`,
        ControlledComponent: (row) =>
          isSMC ? (
            <TooltipWrapper value={row?.utmSource} />
          ) : (
            <InlineEditCell
              value={row?.utmSource}
              defaultEmptyValue="direct"
              className="text-body-xs text-default"
              handleSubmit={(value) => {
                if (row?.utmSource === value) return
                updateContact({ id: row?.id, utmSource: value })
              }}
              triggerEditSideEffect={closeDrawerHandler}
            />
          ),
      },
    },
    {
      id: `utmMedium`,
      header: `Medium`,
      accessorKey: `utmMedium`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `utmMedium.keyword`,
        tooltipClassName: `lowercase`,
        ControlledComponent: (row) =>
          isSMC ? (
            <TooltipWrapper value={row?.utmMedium} />
          ) : (
            <InlineEditCell
              value={row?.utmMedium}
              defaultEmptyValue="(not set)"
              className="text-body-xs text-default"
              handleSubmit={(value) => {
                if (row?.utmMedium === value) return
                updateContact({ id: row?.id, utmMedium: value })
              }}
              triggerEditSideEffect={closeDrawerHandler}
            />
          ),
      },
    },
    {
      id: `utmCampaign`,
      header: `Campaign`,
      accessorKey: `utmCampaign`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `utmCampaign.keyword`,
        tooltipClassName: `lowercase`,
        ControlledComponent: (row) =>
          isSMC ? (
            <TooltipWrapper value={row?.utmCampaign} />
          ) : (
            <InlineEditCell
              value={row?.utmCampaign}
              defaultEmptyValue="(not set)"
              className="text-body-xs text-default"
              handleSubmit={(value) => {
                if (row?.utmCampaign === value) return
                updateContact({ id: row?.id, utmCampaign: value })
              }}
              triggerEditSideEffect={closeDrawerHandler}
            />
          ),
      },
    },
    {
      id: `content`,
      accessorKey: `utmContent`,
      header: `Content`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `utmContent.keyword`,
        tooltipClassName: `lowercase`,
      },
    },
    {
      id: `reportedAttribution`,
      header: `Reported Attribution`,
      accessorKey: `reportedAttribution`,
      meta: {
        getCellStyle: () => `lowercase`,
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `reportedAttribution.keyword`,
      },
    },
  ]
  let columnsWithPermissions: ColumnDef<ContactJson, any>[] = []
  if (staffV2Enabled) {
    addMarketing = isSuper || checkPermission(`org:marketing:advanced-filters`)
    addUtm = !isSMC && (isSuper || checkPermission(`org:contacts:see-utm`))
    columnsWithPermissions = compact([...(addMarketing ? marketingColumns : []), ...(addUtm ? utmColumns : [])])
  } else {
    columnsWithPermissions = compact(addMarketingFilters ? [...marketingColumns, ...utmColumns] : [])
  }
  return [
    {
      id: `name`,
      accessorKey: `name`,
      header: `Name`,
      enableResizing: true,
      meta: {
        group: `General`,
        defaultVisible: true,
        pinned: true,
        sortKey: `name.plain`,
        ControlledComponent: (row) => (
          <AnchorCell
            href={currentUrlWithParam(`drawer`, row?.id!)}
            tooltipMessage={row?.name ?? ``}
            clickHandler={() => contactHandler(row?.id!, row?.patientIds)}
          >
            <ContactCell contact={row as ContactJson} />
          </AnchorCell>
        ),
      },
      enableHiding: false,
      minSize: 100,
    },
    {
      id: `Patient Name`,
      accessorKey: `patientName`,
      header: `Patient Name`,
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `patientName.keyword`,
      },
    },
    {
      id: `Preferred Name`,
      accessorKey: `preferredName`,
      header: `Preferred Name`,
      meta: {
        group: `Communication`,
        defaultVisible: false,
        sortKey: `preferredName.keyword`,
      },
    },
    {
      id: `Name Source`,
      accessorKey: `nameSource`,
      header: `Name Source`,
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `nameSource.keyword`,
      },
    },
    {
      id: `workspaceId`,
      accessorKey: `workspaceId`,
      header: `Workspace`,
      cell: (props) => <LazyWorkspaceCell workspaceId={props.row.original.workspaceId} />,
      meta: {
        group: `General`,
        defaultVisible: false,
      },
      enableSorting: false,
    },
    {
      id: `locationId`,
      accessorKey: `locationId`,
      header: `Location`,
      cell: (props) => <LazyLocationCell locationId={props.row.original.locationId!} />,
      meta: {
        group: `General`,
        defaultVisible: true,
      },
      enableSorting: false,
    },
    {
      id: `tags`,
      accessorFn: (row) => row.tags,
      header: `Tags`,
      meta: {
        group: `General`,
        defaultVisible: true,
        ControlledComponent: (row) => (
          <TagsCell tags={row?.tags} workspaceId={row?.workspaceId} model="contacts" modelId={row?.id!} />
        ),
      },
      enableSorting: false,
    },
    {
      id: `homePhone`,
      accessorKey: `homePhone`,
      header: `Home Phone`,
      meta: {
        group: `Communication`,
        defaultVisible: false,
        pinned: false,
        sortKey: `homePhone.keyword`,
      },
      cell: (props) => <PhoneNumber number={props.row.original.homePhone} />,
    },
    {
      id: `cellPhone`,
      accessorKey: `cellPhone`,
      header: `Cell Phone`,
      cell: (props) => <PhoneNumber number={props.row.original.cellPhone} />,
      meta: {
        group: `Communication`,
        defaultVisible: false,
        pinned: false,
        sortKey: `cellPhone.keyword`,
      },
    },
    {
      id: `workPhone`,
      accessorKey: `workPhone`,
      header: `Work Phone`,
      meta: {
        group: `Communication`,
        defaultVisible: false,
        pinned: false,
        sortKey: `workPhone.keyword`,
      },
      cell: (props) => <PhoneNumber number={props.row.original.workPhone} />,
    },
    {
      id: `Other Phone`,
      accessorKey: `otherPhone`,
      header: `Other Phone`,
      meta: {
        group: `Communication`,
        defaultVisible: false,
        sortKey: `otherPhone.keyword`,
      },
    },
    {
      id: `email`,
      accessorKey: `email`,
      header: `Email`,
      meta: {
        group: `Communication`,
        defaultVisible: false,
        sortKey: `email.keyword`,
      },
    },
    {
      id: `address`,
      accessorKey: `addressLine1`,
      header: `Address`,
      cell: Address,
      meta: {
        group: `Communication`,
        defaultVisible: false,
        sortKey: `addressLine1.keyword`,
      },
    },
    {
      id: `city`,
      accessorKey: `city`,
      header: `City`,
      meta: {
        group: `Communication`,
        defaultVisible: false,
        sortKey: `city.keyword`,
      },
    },
    {
      id: `State`,
      accessorKey: `state`,
      header: `State`,
      meta: {
        group: `Communication`,
        defaultVisible: false,
        sortKey: `state.keyword`,
      },
    },
    {
      id: `zip`,
      accessorKey: `zip`,
      header: `Zipcode`,
      meta: {
        group: `Communication`,
        defaultVisible: false,
        sortKey: `zip.keyword`,
      },
    },
    {
      id: `birthDate`,
      accessorKey: `birthDate`,
      header: `Age`,
      cell: Age,
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `birthDate`,
      },
    },
    {
      id: `Gender`,
      accessorKey: `gender`,
      header: `Gender`,
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `gender.keyword`,
      },
    },
    {
      id: `Patient Linked`,
      accessorKey: `patientIds`,
      header: `Patient Linked`,
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `patientIds.keyword`,
      },
      cell: (props) => (
        <span>
          {props.getValue() ? (
            <CheckCircleIcon className="h-5 w-5 fill-current text-blue-400" />
          ) : (
            <XmarkCircle className="h-5 w-5 text-red-400" />
          )}
        </span>
      ),
    },
    {
      id: `Link Status`,
      accessorKey: `linkStatus`,
      header: `Link Status`,
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `linkStatus.keyword`,
      },
    },
    {
      id: `firstAppointmentDate`,
      accessorKey: `firstAppointmentDate`,
      header: `First Appointment Date`,
      cell: (props) => <DateComponent value={props.row.original.firstAppointmentDate} dateOnly />,
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `firstAppointmentDate`,
      },
    },
    {
      id: `lastAppointmentDate`,
      accessorKey: `lastAppointmentDate`,
      header: `Last Appointment Date`,
      cell: (props) => <DateComponent value={props.row.original.lastAppointmentDate} dateOnly />,
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `lastAppointmentDate`,
      },
    },
    {
      id: `Nearest Appointment Date`,
      accessorKey: `nearestAppointmentDate`,
      header: `Nearest Appointment Date`,
      cell: (props) => <DateComponent value={props.row.original.nearestAppointmentDate} dateOnly />,
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `nearestAppointmentDate`,
      },
    },
    {
      id: `appointmentStatus`,
      accessorKey: `appointmentStatus`,
      header: `Appointment Status`,
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `appointmentStatus.keyword`,
      },
    },
    {
      id: `patientReferral`,
      accessorKey: `patientReferral`,
      header: `Patient Referral`,
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `patientReferral.keyword`,
      },
    },
    {
      id: `collectedLtv`,
      accessorKey: `collectedLtv`,
      accessorFn: (row) => `$${row.collectedLtv?.toLocaleString(`en-US`) ?? 0}`,
      cell: (info) => info.getValue(),
      header: `Collected`,
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `collectedLtv`,
      },
    },
    {
      id: `productionLtv`,
      accessorKey: `productionLtv`,
      accessorFn: (row) => `$${row.productionLtv?.toLocaleString(`en-US`) ?? 0}`,
      cell: (info) => info.getValue(),
      header: `Net Production`,
      meta: {
        group: `General`,
        defaultVisible: true,
        sortKey: `productionLtv`,
      },
    },
    {
      id: `First Visit Value`,
      accessorKey: `fvv`,
      accessorFn: (row) => `$${row.fvv?.toLocaleString(`en-US`) ?? 0}`,
      cell: (info) => info.getValue(),
      header: `First Visit Value`,
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `fvv`,
      },
    },
    {
      id: `Scheduled Production`,
      accessorKey: `scheduledProduction`,
      cell: (info) => `$${info.getValue()?.toLocaleString(`en-US`) ?? 0}`,
      header: `Scheduled Production`,
      meta: {
        group: `General`,
        defaultVisible: true,
        sortKey: `scheduledProduction`,
      },
    },
    {
      id: `leadStatus`,
      accessorKey: `leadStatus`,
      header: `Lead Status`,
      cell: (props) => (
        <>
          <span
            className="mr-1.5 inline-block h-2 w-2 rounded-[2px] align-middle"
            style={{ backgroundColor: getLeadStatusColorMap(props.row.original.leadStatus) }}
          ></span>
          <span>{props.row.original.leadStatus}</span>
        </>
      ),
      meta: {
        group: `Marketing`,
        defaultVisible: true,
        sortKey: `leadStatus.keyword`,
      },
    },
    ...(isSuper
      ? [
          {
            id: `neededAction`,
            accessorFn: (row) => (row.neededAction ? `Yes` : `No`),
            header: `Needed Action`,
            meta: {
              group: `Marketing`,
              defaultVisible: true,
              sortKey: `neededAction`,
            },
          },
        ]
      : []),
    {
      id: `closedLostReason`,
      accessorKey: `closedLostReason`,
      header: `Closed Lost Reason`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `closedLostReason.keyword`,
      },
    },
    {
      id: `channel`,
      accessorKey: `channel`,
      header: `Channel`,
      meta: {
        group: `Marketing`,
        defaultVisible: true,
        sortKey: `channel.keyword`,
        ControlledComponent: (row) => <ChannelMappingCell contact={row} />,
      },
    },
    {
      id: `referralURL`,
      accessorKey: `referralURL`,
      header: `Referral URL`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `referralURL.keyword`,
        tooltipClassName: `lowercase`,
      },
      enableSorting: false,
    },
    {
      id: `firstContactReason`,
      accessorKey: `firstContactReason`,
      header: `First Contacted Reason`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `firstContactReason.keyword`,
      },
    },
    {
      id: `id`,
      accessorKey: `id`,
      header: `Contact ID`,
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `id.keyword`,
      },
    },
    {
      id: `lifecycleStage`,
      accessorKey: `lifecycleStage`,
      header: `Lifecycle Stage`,
      meta: {
        group: `General`,
        defaultVisible: true,
        pinned: false,
        sortKey: `lifecycleStage.keyword`,
      },
    },
    {
      id: `leadOwner`,
      header: `Contact Owner`,
      accessorKey: `leadOwner`,
      meta: {
        group: `General`,
        defaultVisible: false,
        pinned: false,
        sortKey: `leadOwner.name.keyword`,
        tooltipClassName: `overflow-visible`,
        ControlledComponent: (row) =>
          staffDashboardV2 ? (
            <UserAssignCell
              user={row?.leadOwner as any}
              contactId={row?.id}
              locationId={row?.locationId}
              workspaceId={row?.workspaceId}
              firstNameInitial
              onChange={(val?: OptionType) => {
                bg(async () => {
                  if (!val?.value) return
                  if (!row?.id) return
                  await gqlMutation(UPDATE_CONTACT_MUTATION, { id: row.id, input: { leadOwner: val.value } })
                  notifySuccess(`Contact Owner updated successfully`)
                })
              }}
            />
          ) : (
            <StaffAssignCell
              {...row}
              firstNameInitial
              onChange={(val?: OptionType) => {
                bg(async () => {
                  if (!val?.value) return
                  if (!row?.id) return
                  await gqlMutation(UPDATE_CONTACT_MUTATION, { id: row.id, input: { leadOwner: val.value } })
                  notifySuccess(`Contact Owner updated successfully`)
                })
              }}
            />
          ),
      },
    },
    {
      id: `Converted By`,
      accessorKey: `convertedBy`,
      header: `Converted By`,
      cell: (props) => <span>{props.row.original.convertedBy?.name}</span>,
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `convertedBy.name.keyword`,
      },
    },
    {
      id: `status`,
      accessorFn: (props) => props.patientStatus,
      header: `Patient Status`,
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `patientStatus.keyword`,
      },
    },
    {
      id: `Is Converted`,
      accessorKey: `isConverted`,
      header: `Is Converted`,
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `isConverted`,
      },
    },
    {
      id: `Is Qualified`,
      accessorKey: `isQualified`,
      header: `Qualification Status`,
      cell: (props) => (props.getValue() === true ? `Qualified` : props.getValue() === false ? `Not Qualified` : ``),
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `isQualified`,
      },
    },
    {
      id: `firstActivityDate`,
      accessorKey: `firstActivityDate`,
      header: `First Activity Date`,
      cell: (props) => <DateComponent value={props.row.original.firstActivityDate} dateOnly />,
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `firstActivityDate`,
      },
    },
    {
      id: `lastActivityDate`,
      accessorKey: `lastActivityDate`,
      header: `Last Activity Date`,
      cell: (props) => <DateComponent value={props.row.original.lastActivityDate} dateOnly />,
      meta: {
        group: `General`,
        defaultVisible: false,
        pinned: false,
        sortKey: `lastActivityDate`,
      },
    },
    {
      id: `preferredContact`,
      accessorKey: `preferredContact`,
      header: `Preferred Contact`,
      meta: {
        sortKey: `preferredContact.keyword`,
        group: `Communication`,
        defaultVisible: false,
        pinned: false,
      },
    },
    {
      id: `preferredCommunicationMethod`,
      accessorKey: `preferredCommunicationMethod`,
      header: `Preferred Communication Method`,
      meta: {
        group: `Communication`,
        defaultVisible: false,
        pinned: false,
        sortKey: `preferredCommunicationMethod.keyword`,
      },
    },
    {
      id: `becameLeadDate`,
      accessorKey: `becameLeadDate`,
      header: `Became a Lead Date`,
      cell: (props) => DateComponent({ value: props.row.original.becameLeadDate, dateOnly: true }),
      meta: {
        group: `Marketing`,
        defaultVisible: true,
        sortKey: `becameLeadDate`,
      },
    },
    {
      id: `firstAppointmentMadeDate`,
      accessorKey: `firstAppointmentMadeDate`,
      header: `Became a New Patient Scheduled Date`,
      cell: (props) => DateComponent({ value: props.row.original.firstAppointmentMadeDate, dateOnly: true }),
      meta: {
        group: `Marketing`,
        defaultVisible: true,
        sortKey: `firstAppointmentMadeDate`,
      },
    },
    {
      id: `becamePatientDate`,
      accessorKey: `becamePatientDate`,
      header: `Became a Patient Date`,
      cell: (props) => DateComponent({ value: props.row.original.becamePatientDate, dateOnly: true }),
      meta: {
        group: `PMS`,
        defaultVisible: true,
        sortKey: `becamePatientDate`,
      },
    },
    {
      id: `patientCreatedDate`,
      accessorKey: `patientCreatedDate`,
      header: `Patient Created Date`,
      cell: (props) => DateComponent({ value: props.row.original.patientCreatedDate, dateOnly: true }),
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `patientCreatedDate`,
      },
    },
    {
      id: `becameInactiveDate`,
      header: `Became Inactive Date`,
      cell: (props) => DateComponent({ value: props.row.original.becameInactiveDate, dateOnly: true }),
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `becameInactiveDate`,
      },
    },
    {
      id: `firstActivityType`,
      header: `First Activity Type`,
      accessorKey: `firstActivityType`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `firstActivityType.keyword`,
      },
    },
    {
      id: `First Transaction Date`,
      accessorKey: `firstTransactionDate`,
      header: `First Transaction Date`,
      cell: (props) => DateComponent({ value: props.row.original.firstTransactionDate, dateOnly: true }),
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `firstTransactionDate`,
      },
    },
    {
      id: `Last Transaction Date`,
      accessorKey: `lastTransactionDate`,
      header: `Last Transaction Date`,
      cell: (props) => DateComponent({ value: props.row.original.lastTransactionDate, dateOnly: true }),
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `lastTransactionDate`,
      },
    },
    {
      id: `Ad Click Date`,
      accessorKey: `adClickDate`,
      header: `Ad Click Date`,
      cell: (props) => DateComponent({ value: props.row.original.adClickDate, dateOnly: true }),
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `adClickDate`,
      },
    },
    {
      id: `Needs Review`,
      accessorKey: `needsReview`,
      header: `Needs Review`,
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `needsReview`,
      },
    },
    {
      id: `Recare Count`,
      accessorKey: `recareCount`,
      header: `Recare Count`,
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `recareCount`,
      },
    },
    {
      id: `schedulingActivityType`,
      header: `Scheduling Activity Type`,
      accessorKey: `schedulingActivityType`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `schedulingActivityType.keyword`,
      },
    },
    {
      id: `Has Activity`,
      header: `Has Activity`,
      accessorFn: (props) => props.activityTypes,
      cell: (props) => <>{props.getValue()?.join(`, `)}</>,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
      },
      enableSorting: false,
    },
    ...columnsWithPermissions,
  ]
}
